import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  connectSocket,
  disconnectSocket,
  getSocket,
  joinGame,
  leaveGame,
  startTyping,
  stopTyping,
  emitMessage,
  emitTurnChange,
  emitDiceRoll,
  emitGameUpdate,
  onPlayerJoined,
  onPlayerLeft,
  onPlayerKicked,
  onPlayerBanned,
  onPlayerDisconnected,
  onUserTyping,
  onUserStoppedTyping,
  onNewMessage,
  onTurnUpdate,
  onDiceResult,
  onGameStateUpdate,
  onGameState,
  onTurnTimerWarning,
  onTurnAutoEnded
} from '../services/socket';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);

  // Connect socket when user is authenticated
  const connect = useCallback((token) => {
    const socket = connectSocket(token);

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    return socket;
  }, []);

  // Disconnect socket
  const disconnect = useCallback(() => {
    disconnectSocket();
    setIsConnected(false);
    setCurrentGameId(null);
    setTypingUsers([]);
  }, []);

  // Join a game room
  const joinGameRoom = useCallback((gameId) => {
    if (currentGameId && currentGameId !== gameId) {
      leaveGame(currentGameId);
    }
    joinGame(gameId);
    setCurrentGameId(gameId);
    setTypingUsers([]);
  }, [currentGameId]);

  // Leave current game room
  const leaveGameRoom = useCallback(() => {
    if (currentGameId) {
      leaveGame(currentGameId);
      setCurrentGameId(null);
      setTypingUsers([]);
    }
  }, [currentGameId]);

  // Typing indicator with debounce
  const [typingTimeout, setTypingTimeout] = useState(null);

  const handleTyping = useCallback(() => {
    if (!currentGameId) return;

    startTyping(currentGameId);

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout to stop typing after 2 seconds of inactivity
    const timeout = setTimeout(() => {
      stopTyping(currentGameId);
    }, 2000);

    setTypingTimeout(timeout);
  }, [currentGameId, typingTimeout]);

  const handleStopTyping = useCallback(() => {
    if (!currentGameId) return;
    stopTyping(currentGameId);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
  }, [currentGameId, typingTimeout]);

  // Set up typing indicator listeners
  useEffect(() => {
    if (!isConnected) return;

    const handleUserTyping = ({ userId, username }) => {
      setTypingUsers(prev => {
        if (prev.some(u => u.userId === userId)) return prev;
        return [...prev, { userId, username }];
      });
    };

    const handleUserStoppedTyping = ({ userId }) => {
      setTypingUsers(prev => prev.filter(u => u.userId !== userId));
    };

    const unsubTyping = onUserTyping(handleUserTyping);
    const unsubStoppedTyping = onUserStoppedTyping(handleUserStoppedTyping);

    return () => {
      unsubTyping();
      unsubStoppedTyping();
    };
  }, [isConnected]);

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  const value = {
    // State
    isConnected,
    currentGameId,
    typingUsers,

    // Connection
    connect,
    disconnect,
    getSocket,

    // Game rooms
    joinGameRoom,
    leaveGameRoom,

    // Typing
    handleTyping,
    handleStopTyping,

    // Emitters
    emitMessage: (message) => emitMessage(currentGameId, message),
    emitTurnChange: (turnState, previousPlayer, currentPlayer) =>
      emitTurnChange(currentGameId, turnState, previousPlayer, currentPlayer),
    emitDiceRoll: (roll, username, characterName) =>
      emitDiceRoll(currentGameId, roll, username, characterName),
    emitGameUpdate: (update) => emitGameUpdate(currentGameId, update),

    // Event listeners
    onPlayerJoined,
    onPlayerLeft,
    onPlayerKicked,
    onPlayerBanned,
    onPlayerDisconnected,
    onNewMessage,
    onTurnUpdate,
    onDiceResult,
    onGameStateUpdate,
    onGameState,
    onTurnTimerWarning,
    onTurnAutoEnded
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
